/** Milliseconds since the Unix epoch (UTC). Used for instants: issuedAt, expiresAt, created_at. */
export type EpochMs = number;

/**
 * A business-local calendar date, `YYYY-MM-DD`. Always paired with the business timezone;
 * never converted through JS `Date` for plan validity (see CLAUDE.md rule 3).
 */
export type LocalDate = string;

/** IANA timezone name, e.g. "Asia/Kolkata". */
export type TimeZone = string;

/** Source of the current instant. Inject it; never call Date.now() inside logic that is tested. */
export interface Clock {
  now(): EpochMs;
}

const dateFormatters = new Map<TimeZone, Intl.DateTimeFormat>();

/**
 * The business-local calendar date of an instant. This is the one place an instant becomes a
 * `LocalDate`; it formats through Intl (no offset arithmetic), so DST and half-hour zones are
 * the platform's problem, not ours. Throws RangeError for a non-finite instant or unknown zone.
 */
export function localDateOf(epochMs: EpochMs, timeZone: TimeZone): LocalDate {
  if (!Number.isFinite(epochMs)) throw new RangeError(`localDateOf: epochMs must be a finite number, got ${String(epochMs)}`);
  let fmt = dateFormatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", {
      timeZone,
      calendar: "gregory",
      numberingSystem: "latn",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    dateFormatters.set(timeZone, fmt);
  }
  const part = (type: string) => fmt.formatToParts(new Date(epochMs)).find((p) => p.type === type)?.value ?? "";
  return `${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`;
}
